import { Icon } from '@iconify/react';
import { Tooltip } from '@nextui-org/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  RootState,
  ChatSessionState,
  updateConversationsSidebarView,
  useAppDispatch,
  useGetConversationHistoryMutation,
} from '@circuitry-ai/doc-data';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

const PAGE_SIZE = 10;

type Props = {
  activeSessionId?: string;
  /** Scopes the list to one advisor; omit to list every advisor for the user. */
  advisorId?: string;
  /** `conversationId` fetches the transcript; `senderId` keys the session. */
  onSelect: (conversationId: string, senderId: string) => void;
};

type SidebarConversation = {
  /** Record ID used by /api/v5/conversations/query/[id]. */
  id: string;
  sender_id: string;
  label: string;
  timestamp: number;
  status?: 'idle' | 'loading' | 'ready' | 'error';
};

const timeOf = (value: unknown): number => {
  const parsed = new Date((value as string) ?? 0).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * The question that opened a chat started in this browser session, used as its
 * label. Restored history carries `isHistorical`, and those turns are skipped so
 * an old conversation keeps the title the API gave it.
 */
const openingQuestionOf = (session: ChatSessionState): string | undefined => {
  const opening = session.chatMessages.find((message: any) => message?.isUser);
  if (!opening || (opening as any).isHistorical) return undefined;
  const text = (opening as any).message;
  if (typeof text !== 'string') return undefined;
  const label = text.replace(/\s+/g, ' ').trim();
  return label || undefined;
};

/**
 * Rows already on screen, kept at module scope so the reveal plays once per
 * conversation. The panel re-mounts whenever a new chat is started, and a ref
 * would reset with it and replay the animation for the whole list.
 */
const revealedConversations = new Set<string>();

type HistoryPages = { items: any[]; total: number; page: number };

/**
 * Pages already fetched, per user and advisor. Starting a new chat re-keys the
 * chat UI and takes the panel down with it, and without this the list would hit
 * the history API again on every new chat. Live conversations come from the
 * store, so a stale page here still shows the new chat immediately.
 */
const historyPages = new Map<string, HistoryPages>();

export function ConversationsSidebar({
  activeSessionId,
  advisorId,
  onSelect,
}: Readonly<Props>) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const listRef = useRef<HTMLDivElement | null>(null);
  const [getHistory, { isLoading }] = useGetConversationHistoryMutation();

  const user = useSelector((state: RootState) => state.auth.user.details);
  const advisorName = useSelector((state: RootState) => state.advisor.activeChatAdvisor?.name);
  const sessions = useSelector((state: RootState) => state.chatAdvisorSliceReducer.sessions);
  const sidebarView = useSelector((state: RootState) => state.chatAdvisorSliceReducer.conversationsSidebar);
  const selectedSessionId = activeSessionId ?? sidebarView?.selectedSessionId ?? undefined;

  // Keyed per advisor so page 2+ never appends another advisor's rows, and so
  // switching advisors and back reuses what was already fetched.
  const cacheKey = `${user?.sub ?? ''}|${advisorId ?? ''}`;
  const [pages, setPages] = useState<HistoryPages>(
    () => historyPages.get(cacheKey) ?? { items: [], total: 0, page: 1 }
  );
  const { items: history, total, page } = pages;

  useEffect(() => {
    setPages(historyPages.get(cacheKey) ?? { items: [], total: 0, page: 1 });
  }, [cacheKey]);

  // Recent history for the signed-in user, scoped to this advisor; the API expects filter arrays.
  useEffect(() => {
    if (!user?.sub) return undefined;
    // Already fetched, so this is a re-mount rather than a request for more.
    if ((historyPages.get(cacheKey)?.page ?? 0) >= page) return undefined;

    let cancelled = false;
    const filters: Record<string, unknown>[] = [{ user_id: user.sub, condition: 'equals' }];
    if (advisorId) filters.push({ advisor_id: advisorId, condition: 'equals' });
    getHistory({
      pagination: { page, per_page: PAGE_SIZE },
      filters,
      sorting: { field: 'date', order: 'desc' },
    })
      .unwrap()
      .then((data: any) => {
        if (cancelled) return;
        const conversations = data?.conversations ?? [];
        const previous = historyPages.get(cacheKey)?.items ?? [];
        const next: HistoryPages = {
          items: page === 1 ? conversations : [...previous, ...conversations],
          total: data?.total ?? 0,
          page,
        };
        historyPages.set(cacheKey, next);
        setPages(next);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [advisorId, cacheKey, getHistory, page, user?.sub]);

  // Restore scroll offset when returning from another route/window.
  useEffect(() => {
    const node = listRef.current;
    if (node && sidebarView?.scrollTop) node.scrollTop = sidebarView.scrollTop;
    // Restore once per mount; live scrolling must not fight the user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = useCallback(() => {
    const node = listRef.current;
    if (node) dispatch(updateConversationsSidebarView({ scrollTop: node.scrollTop }));
  }, [dispatch]);

  const conversations = useMemo<SidebarConversation[]>(() => {
    const merged = new Map<string, SidebarConversation>();

    history.forEach((item: any) => {
      const id = String(item?.id ?? item?.sender_id ?? '');
      if (!id) return;
      merged.set(id, {
        id,
        sender_id: item.sender_id,
        label: item.title || item.advisor_name || t('Conversation'),
        timestamp: timeOf(item.date),
      });
    });

    (Object.values(sessions ?? {}) as ChatSessionState[]).forEach((session) => {
      // A started-but-unasked conversation is not a conversation yet; it joins
      // the list the moment its first question is sent.
      if (session.chatMessages.length === 0) return;
      if (advisorId && session.advisorId && session.advisorId !== advisorId) return;

      const historyEntry = [...merged.entries()].find(([, item]) =>
        item.sender_id === (session.senderId ?? session.sessionId)
      );
      const key = historyEntry?.[0] ?? session.sessionId;
      const existing = historyEntry?.[1] ?? merged.get(key);
      merged.set(key, {
        id: key,
        sender_id: session.senderId ?? session.sessionId,
        // A chat started here is named after the question that started it, and
        // keeps that name even once the history API returns a title for it.
        // Restored chats have no question of their own, so they keep the title.
        label: openingQuestionOf(session) ?? existing?.label ?? session.title ?? t('New conversation'),
        timestamp: session.updatedAt ? timeOf(session.updatedAt) : existing?.timestamp ?? Date.now(),
        status: session.status,
      });
    });

    return [...merged.values()].sort((a, b) => b.timestamp - a.timestamp);
  }, [advisorId, history, sessions, t]);

  // Marked after paint so a row's first render still carries the reveal class.
  useEffect(() => {
    conversations.forEach((item) => revealedConversations.add(item.id));
  }, [conversations]);

  const select = (item: SidebarConversation) => {
    // Highlight by sender_id so the row stays selected once the session opens.
    dispatch(updateConversationsSidebarView({ selectedSessionId: item.sender_id }));
    onSelect(item.id, item.sender_id);
  };

  const hasMore = history.length < total;
  const isCollapsed = Boolean(sidebarView?.isCollapsed);
  const toggle = () =>
    dispatch(updateConversationsSidebarView({ isCollapsed: !isCollapsed }));

  const subheading = advisorName
    ? `${t('Your recent conversations with')} ${advisorName}`
    : t('Your recent conversations');

  return (
    <aside
      aria-label={t('My conversations')}
      aria-hidden={isCollapsed}
      className={`hidden h-full min-h-0 shrink-0 flex-col overflow-hidden bg-background text-sm transition-[width,visibility] duration-300 ease-out md:flex ${
        isCollapsed ? 'invisible w-0' : 'visible w-72 border-r border-default-200'
      }`}
    >
      <div className="flex h-full min-h-0 w-72 flex-col">
          <div className="flex items-start justify-between gap-2 p-3 shrink-0">
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-tight">{t('My conversations')}</p>
              <p className="truncate text-sm text-default-500" title={subheading}>{subheading}</p>
            </div>
            <button
              type="button"
              onClick={toggle}
              aria-expanded
              aria-label={t('Hide conversations')}
              className="p-1 rounded hover:bg-default-100 shrink-0"
            >
              <Icon icon="mdi:chevron-double-left" aria-hidden />
            </button>
          </div>          
          <div ref={listRef} onScroll={handleScroll} className="flex-1 min-h-0 overflow-y-auto">
            {conversations.map((item) => (
              <button
                type="button"
                key={item.id}
                onClick={() => select(item)}
                aria-current={selectedSessionId === item.sender_id}
                className={`w-full text-left p-3 border-t border-default-100 hover:bg-default-100 ${
                  selectedSessionId === item.sender_id ? 'bg-default-100' : ''
                } ${revealedConversations.has(item.id) ? '' : 'animate-[conversationReveal_0.35s_ease-out]'}`}
              >
                <div className="flex items-center gap-2">
                  <span className="truncate" title={item.label}>{item.label}</span>
                  {item.status === 'loading' && (
                    <span
                      title={t('Generating')}
                      aria-label={t('Generating')}
                      className="ml-auto h-2 w-2 shrink-0 rounded-full bg-success animate-[conversationPulse_1s_ease-in-out_infinite]"
                    />
                  )}
                  {item.status === 'ready' && (
                    <span
                      title={t('Response ready')}
                      aria-label={t('Response ready')}
                      className="ml-auto shrink-0 text-success"
                    >
                      <Icon icon="mdi:check-circle" aria-hidden />
                    </span>
                  )}
                </div>
              </button>
            ))}
            {isLoading && <div className="p-3 text-default-500">{t('Loading…')}</div>}
            {hasMore && !isLoading && (
              <button
                type="button"
                className="w-full p-3 text-primary"
                onClick={() => setPages((value) => ({ ...value, page: value.page + 1 }))}
              >
                {t('Show older')}
              </button>
            )}
          </div>
      </div>
    </aside>
  );
}

/**
 * The pull handle that opens the panel. It lives outside the message scroller,
 * pinned to the middle of the chat area, so it stays put however far the
 * thread is scrolled and never rides up into the advisor name or the tabs.
 */
export function ConversationsSidebarHandle() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isCollapsed = useSelector(
    (state: RootState) => state.chatAdvisorSliceReducer.conversationsSidebar?.isCollapsed
  );
  if (!isCollapsed) return null;

  return (
    <Tooltip content={t('Open history')} placement="right" delay={300}>
      <button
        type="button"
        onClick={() => dispatch(updateConversationsSidebarView({ isCollapsed: false }))}
        aria-expanded={false}
        aria-label={t('Open history')}
        className="absolute left-0 top-1/2 z-30 hidden md:flex h-16 w-6 -translate-y-1/2 items-center justify-center rounded-r-xl border border-l-0 border-default-200 bg-background text-default-500 shadow-md transition-colors hover:bg-default-100 hover:text-foreground"
      >
        <Icon icon="mdi:chevron-double-right" aria-hidden />
      </button>
    </Tooltip>
  );
}

export default ConversationsSidebar;
